// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: e5925409-739a-4540-86ee-50d146b3107a

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, TextField, MenuItem, Chip, Tooltip, Button, Tabs, Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { sectionHeaderColor } from './ThemeProvider';
import DownloadIcon from '@mui/icons-material/Download';
import { PICKLISTS, resolveDrTier } from './schema';
import { printElementToPdf } from './exports';
import store from './store';
import HelpBubble from './HelpBubble';
import { TabLabel } from './ui';

// ---------------------------------------------------------------------------
// Primordia — LeanIX-style Matrix + Interface Circle Map.
//
//  TAB 1  "Matrix"                 X/Y heat map. Any two dimensions (tags or
//                                  fields) crossed; cells shaded by app count
//                                  or by average data completion.
//  TAB 2  "Interface Circle Map"   Applications laid out on a circle with their
//                                  integrations drawn as chords through the
//                                  centre, coloured by flow direction.
//
// No charting library — plain SVG + MUI so the CRA build stays dependency-free.
// ---------------------------------------------------------------------------

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEADER = 'sectionHeader.main';
const EMPTY = '—';

const DIR_COLORS = { outbound: '#F5A623', inbound: '#2E7D32', bidirectional: BLUE };
const EMPTY_SET = new Set(); // shared sentinel — avoids allocating a Set per unmatched node
const dirOf = (it) => (DIR_COLORS[it.direction] ? it.direction : 'outbound');

const tagVal = (a, group) => (a.tags || []).find((t) => t.group === group)?.value;

// Selectable axes — tags first, then fact-sheet fields.
//
// DR Tier is the one axis with two vocabularies in real data: imports write the
// "DR Tier: N" tag, templates write fields.drTier = "Tier N". Reading the tag
// alone left every template-created application on the "—" row while its own
// fact sheet showed the tier, so it goes through resolveDrTier(), the shared
// reconciler in schema.js (also used by Inventory, Landscape and Portfolio).
// It returns the normalised "Tier N", hence PICKLISTS.tier — not PICKLISTS.drTier,
// whose "DR Tier: N" entries would no longer match and would silently drop the
// axis back to alphabetical order.
//
// The other tag axes (Cell, Managed By) have no field counterpart in the schema
// — they exist only as tag groups — so the tag really is their single source.
const DIMENSIONS = [
  { key: 'cell', label: 'Cell', get: (a) => tagVal(a, 'Cell') || EMPTY },
  { key: 'drTier', label: 'DR Tier', get: (a) => resolveDrTier(a) || EMPTY, order: PICKLISTS.tier },
  { key: 'managedBy', label: 'Managed By', get: (a) => tagVal(a, 'Managed By') || EMPTY },
  { key: 'applicationType', label: 'Application Type', get: (a) => a.fields?.applicationType || EMPTY, order: PICKLISTS.applicationType },
  { key: 'time', label: 'TIME', get: (a) => a.fields?.timeClassification || EMPTY, order: PICKLISTS.time },
  { key: 'sixR', label: '6R', get: (a) => a.fields?.sixRClassification || EMPTY, order: PICKLISTS.sixR },
  { key: 'lifecycle', label: 'Lifecycle', get: (a) => a.fields?.lifecycleState || EMPTY, order: PICKLISTS.lifecycle },
  { key: 'hosting', label: 'Hosting', get: (a) => a.fields?.hostingType || EMPTY, order: PICKLISTS.hostingType },
];

const dimension = (key) => DIMENSIONS.find((d) => d.key === key) || DIMENSIONS[0];

// Sort axis values by their picklist order when we have one; unknowns alpha, EMPTY last.
function sortValues(values, order) {
  const idx = (v) => {
    if (v === EMPTY) return 10000;
    const i = order ? order.indexOf(v) : -1;
    return i >= 0 ? i : 5000;
  };
  return [...values].sort((a, b) => (idx(a) - idx(b)) || a.localeCompare(b));
}

// ---- Colour helpers --------------------------------------------------------
const hexToRgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const toHex = (n) => Math.round(n).toString(16).padStart(2, '0');
const mix = (from, to, t) => {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  return `#${a.map((c, i) => toHex(c + (b[i] - c) * Math.max(0, Math.min(1, t)))).join('')}`;
};
// light -> dark blue by relative count
const countColor = (n, max) => (n <= 0 ? '#F7F9FB' : mix('#E6EFF8', NAVY, max <= 1 ? 1 : n / max));
const completionColor = (pct) => (pct >= 75 ? '#2E7D32' : pct >= 40 ? '#F5A623' : '#D0021B');

const COLOR_MODES = [
  { key: 'count', label: 'Application count' },
  { key: 'completion', label: 'Avg. data completion' },
];

export default function MatrixReport({ tenantId, onOpen }) {
  const theme = useTheme();
  const [apps, setApps] = useState([]);
  const [tab, setTab] = useState(0);
  const [xDim, setXDim] = useState('cell');
  const [yDim, setYDim] = useState('time');
  const [colorMode, setColorMode] = useState('count');
  const [selected, setSelected] = useState(null); // { x, y }
  const [hoverId, setHoverId] = useState(null);

  useEffect(() => {
    let alive = true;
    store.listFactSheets(tenantId, { type: 'Application' }).then((rows) => { if (alive) setApps(rows); });
    return () => { alive = false; };
  }, [tenantId]);

  useEffect(() => { setSelected(null); }, [xDim, yDim, tenantId]);

  const open = (id) => { if (onOpen) onOpen(id); };

  // ---- Matrix model --------------------------------------------------------
  const matrix = useMemo(() => {
    const xd = dimension(xDim);
    const yd = dimension(yDim);
    const cells = {};
    const xs = new Set();
    const ys = new Set();
    apps.forEach((a) => {
      const x = xd.get(a);
      const y = yd.get(a);
      xs.add(x);
      ys.add(y);
      const k = `${x}||${y}`;
      (cells[k] = cells[k] || []).push(a);
    });
    const xValues = sortValues([...xs], xd.order);
    const yValues = sortValues([...ys], yd.order);
    let max = 0;
    Object.values(cells).forEach((list) => { if (list.length > max) max = list.length; });
    return { xd, yd, xValues, yValues, cells, max };
  }, [apps, xDim, yDim]);

  const cellApps = (x, y) => matrix.cells[`${x}||${y}`] || [];
  const avgCompletion = (list) => (list.length
    ? Math.round(list.reduce((s, a) => s + (a.completion || 0), 0) / list.length)
    : 0);

  const selectedApps = selected ? cellApps(selected.x, selected.y) : [];

  // ---- Circle map model ----------------------------------------------------
  const circle = useMemo(() => {
    const byName = new Map();
    apps.forEach((a) => byName.set(String(a.name || '').trim().toLowerCase(), a));

    const links = [];
    let resolved = 0;
    const unresolvedTargets = new Set();
    const involved = new Set();

    apps.forEach((a) => {
      (a.integrations || []).forEach((it, i) => {
        const key = String(it.to || '').trim().toLowerCase();
        const target = key ? byName.get(key) : null;
        if (target && target.id !== a.id) {
          resolved += 1;
          involved.add(a.id);
          involved.add(target.id);
          links.push({
            id: `${a.id}-${target.id}-${i}`,
            from: a.id,
            to: target.id,
            fromName: a.name,
            toName: target.name,
            direction: dirOf(it),
            protocol: it.protocol || '',
            dataType: it.dataType || '',
          });
        } else if (it.to) {
          unresolvedTargets.add(it.to);
        }
      });
    });

    // Every application that has integrations belongs on the ring, plus anything
    // another application points at (so no chord dangles off-circle).
    const nodeApps = apps
      .filter((a) => involved.has(a.id) || (a.integrations || []).length > 0)
      .sort((a, b) => a.name.localeCompare(b.name));

    const size = 820;
    const c = size / 2;
    const R = 285;
    const pos = {};
    nodeApps.forEach((a, i) => {
      const angle = (-Math.PI / 2) + (i * 2 * Math.PI) / Math.max(1, nodeApps.length);
      pos[a.id] = {
        x: c + R * Math.cos(angle),
        y: c + R * Math.sin(angle),
        lx: c + (R + 14) * Math.cos(angle),
        ly: c + (R + 14) * Math.sin(angle),
        deg: (angle * 180) / Math.PI,
        left: Math.cos(angle) < 0,
      };
    });

    const drawable = links.filter((l) => pos[l.from] && pos[l.to]);
    const degree = {};
    // Adjacency, keyed by node id, built once here rather than with
    // `links.some(...)` per node on every hover — that was an O(nodeApps ×
    // links) scan re-run on every mouseenter, and re-rendered the whole SVG.
    const neighbors = new Map();
    drawable.forEach((l) => {
      degree[l.from] = (degree[l.from] || 0) + 1;
      degree[l.to] = (degree[l.to] || 0) + 1;
      if (!neighbors.has(l.from)) neighbors.set(l.from, new Set());
      if (!neighbors.has(l.to)) neighbors.set(l.to, new Set());
      neighbors.get(l.from).add(l.to);
      neighbors.get(l.to).add(l.from);
    });

    return {
      size,
      c,
      nodeApps,
      pos,
      links: drawable,
      degree,
      neighbors,
      resolved,
      unresolved: unresolvedTargets.size,
      unresolvedNames: [...unresolvedTargets].sort(),
    };
  }, [apps]);

  const labelSize = circle.nodeApps.length > 70 ? 7 : circle.nodeApps.length > 45 ? 8 : 10;

  // ---- Render --------------------------------------------------------------
  const emptyState = (msg) => (
    <Paper sx={{ p: 6, textAlign: 'center', color: GREY }}>
      <Typography sx={{ fontWeight: 700, color: HEADER, mb: 0.5 }}>Nothing to plot yet</Typography>
      <Typography variant="body2">{msg}</Typography>
    </Paper>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEADER }}>Matrix &amp; Interfaces</Typography>
        <HelpBubble topic="matrixReport" />
        <Box sx={{ flex: 1 }} />
        <Button
          startIcon={<DownloadIcon />}
          variant="outlined"
          onClick={() => (tab === 0
            ? printElementToPdf('matrix-print', 'Application Matrix')
            : printElementToPdf('circlemap-print', 'Interface Circle Map'))}
        >
          PDF
        </Button>
      </Stack>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e6eaee' }}>
        <Tab label={<TabLabel text="Matrix" topic="tabMatrix" />} sx={{ fontWeight: 700 }} />
        <Tab label={<TabLabel text="Interface Circle Map" topic="tabInterfaceCircleMap" />} sx={{ fontWeight: 700 }} />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <TextField select size="small" label="X axis" value={xDim} onChange={(e) => setXDim(e.target.value)} sx={{ minWidth: 170 }}>
              {DIMENSIONS.map((d) => <MenuItem key={d.key} value={d.key}>{d.label}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Y axis" value={yDim} onChange={(e) => setYDim(e.target.value)} sx={{ minWidth: 170 }}>
              {DIMENSIONS.map((d) => <MenuItem key={d.key} value={d.key}>{d.label}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Colour by" value={colorMode} onChange={(e) => setColorMode(e.target.value)} sx={{ minWidth: 200 }}>
              {COLOR_MODES.map((m) => <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              {colorMode === 'count' ? (
                <>
                  <Typography variant="caption" sx={{ color: GREY, mr: 0.5 }}>Fewer</Typography>
                  {[0.15, 0.4, 0.65, 0.85, 1].map((t) => (
                    <Box key={t} sx={{ width: 22, height: 12, bgcolor: mix('#E6EFF8', NAVY, t), border: '1px solid #dfe6ee' }} />
                  ))}
                  <Typography variant="caption" sx={{ color: GREY, ml: 0.5 }}>More</Typography>
                </>
              ) : (
                [['≥75%', '#2E7D32'], ['40–74%', '#F5A623'], ['<40%', '#D0021B']].map(([lbl, col]) => (
                  <Chip key={lbl} size="small" label={lbl} sx={{ bgcolor: col, color: '#fff', height: 20, fontSize: 11 }} />
                ))
              )}
            </Stack>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ color: GREY }}>{apps.length} applications</Typography>
          </Stack>

          {!apps.length ? emptyState('No Application fact sheets in this workspace yet. Import or create some to build the matrix.') : (
            <div id="matrix-print">
              <Paper sx={{ p: 2, overflowX: 'auto' }}>
                <Typography sx={{ fontWeight: 700, color: HEADER, mb: 1.5, fontSize: 14 }}>
                  {matrix.yd.label} (rows) × {matrix.xd.label} (columns)
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: `minmax(150px, 200px) repeat(${matrix.xValues.length}, minmax(72px, 1fr)) 64px`,
                  gap: '2px',
                  minWidth: 320 + matrix.xValues.length * 74,
                }}
                >
                  {/* header row */}
                  <Box sx={{ fontSize: 11, fontWeight: 700, color: GREY, display: 'flex', alignItems: 'flex-end', p: 0.5 }}>
                    {matrix.yd.label} \ {matrix.xd.label}
                  </Box>
                  {matrix.xValues.map((x) => (
                    <Box key={x} sx={{ fontSize: 11, fontWeight: 700, color: HEADER, p: 0.5, textAlign: 'center', lineHeight: 1.2 }}>{x}</Box>
                  ))}
                  <Box sx={{ fontSize: 11, fontWeight: 700, color: GREY, p: 0.5, textAlign: 'center' }}>Total</Box>

                  {/* body */}
                  {matrix.yValues.map((y) => {
                    const rowTotal = matrix.xValues.reduce((s, x) => s + cellApps(x, y).length, 0);
                    return (
                      <React.Fragment key={y}>
                        <Box sx={{ fontSize: 11, fontWeight: 700, color: HEADER, p: 0.75, display: 'flex', alignItems: 'center', bgcolor: 'chipMuted.main', borderRadius: 0.5 }}>{y}</Box>
                        {matrix.xValues.map((x) => {
                          const list = cellApps(x, y);
                          const n = list.length;
                          const pct = avgCompletion(list);
                          const isSel = selected && selected.x === x && selected.y === y;
                          const bg = n === 0
                            ? '#F7F9FB'
                            : colorMode === 'completion' ? completionColor(pct) : countColor(n, matrix.max);
                          const dark = n > 0 && (colorMode === 'completion' || n / Math.max(1, matrix.max) > 0.45);
                          const names = list.slice(0, 10).map((a) => a.name);
                          const title = n === 0 ? 'No applications' : (
                            <Box sx={{ fontSize: 11, lineHeight: 1.5 }}>
                              <Box sx={{ fontWeight: 700, mb: 0.5 }}>{y} · {x} — {n} app{n === 1 ? '' : 's'} · avg {pct}% complete</Box>
                              {names.map((nm) => <Box key={nm}>{nm}</Box>)}
                              {n > 10 && <Box sx={{ fontStyle: 'italic' }}>+{n - 10} more</Box>}
                            </Box>
                          );
                          return (
                            <Tooltip key={`${x}||${y}`} title={title} arrow>
                              <Box
                                onClick={() => n > 0 && setSelected(isSel ? null : { x, y })}
                                sx={{
                                  bgcolor: bg,
                                  color: n === 0 ? '#c3ccd5' : dark ? '#fff' : HEADER,
                                  border: isSel ? `2px solid ${BLUE}` : '1px solid #eef2f6',
                                  borderRadius: 0.5,
                                  minHeight: 46,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: n > 0 ? 'pointer' : 'default',
                                  transition: 'opacity .15s',
                                  '&:hover': { opacity: n > 0 ? 0.85 : 1 },
                                }}
                              >
                                <Box sx={{ fontSize: 15, fontWeight: 700 }}>{n || '·'}</Box>
                                {colorMode === 'completion' && n > 0 && (
                                  <Box sx={{ fontSize: 10, opacity: 0.9 }}>{pct}%</Box>
                                )}
                              </Box>
                            </Tooltip>
                          );
                        })}
                        <Box sx={{ fontSize: 12, fontWeight: 700, color: GREY, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{rowTotal}</Box>
                      </React.Fragment>
                    );
                  })}

                  {/* totals row */}
                  <Box sx={{ fontSize: 11, fontWeight: 700, color: GREY, p: 0.75 }}>Total</Box>
                  {matrix.xValues.map((x) => (
                    <Box key={`t-${x}`} sx={{ fontSize: 12, fontWeight: 700, color: GREY, textAlign: 'center', pt: 0.75 }}>
                      {matrix.yValues.reduce((s, y) => s + cellApps(x, y).length, 0)}
                    </Box>
                  ))}
                  <Box sx={{ fontSize: 12, fontWeight: 700, color: HEADER, textAlign: 'center', pt: 0.75 }}>{apps.length}</Box>
                </Box>
              </Paper>

              {selected && (
                <Paper sx={{ p: 2, mt: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 700, color: HEADER, fontSize: 14 }}>
                      {matrix.yd.label}: {selected.y} · {matrix.xd.label}: {selected.x}
                    </Typography>
                    <Chip size="small" label={`${selectedApps.length} app${selectedApps.length === 1 ? '' : 's'}`} sx={{ height: 20 }} />
                    <Chip size="small" label={`avg ${avgCompletion(selectedApps)}% complete`} sx={{ height: 20, bgcolor: '#e8f0fe' }} />
                  </Stack>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {selectedApps.map((a) => (
                      <Tooltip key={a.id} title={`${a.name} · ${a.completion || 0}% complete`}>
                        <Box
                          onClick={() => open(a.id)}
                          sx={{
                            bgcolor: BLUE, color: '#fff', px: 1, py: 0.5, borderRadius: 0.5,
                            fontSize: 11, fontWeight: 600, cursor: 'pointer', maxWidth: 220,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            '&:hover': { bgcolor: NAVY },
                          }}
                        >
                          {a.name}
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                </Paper>
              )}
            </div>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
            {Object.entries(DIR_COLORS).map(([dir, col]) => (
              <Stack key={dir} direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 18, height: 3, bgcolor: col, borderRadius: 2 }} />
                <Typography variant="caption" sx={{ color: GREY, textTransform: 'capitalize' }}>{dir}</Typography>
              </Stack>
            ))}
            <Box sx={{ flex: 1 }} />
            <Chip size="small" label={`${circle.nodeApps.length} applications on ring`} sx={{ height: 22 }} />
            <Chip size="small" label={`${circle.resolved} resolved interfaces`} sx={{ height: 22, bgcolor: '#e6f4ea', color: '#1b5e20' }} />
            <Tooltip title={circle.unresolvedNames.length
              ? `External / unmatched targets: ${circle.unresolvedNames.slice(0, 10).join(', ')}${circle.unresolvedNames.length > 10 ? ` +${circle.unresolvedNames.length - 10} more` : ''}`
              : 'Every integration target matched an application'}
            >
              <Chip size="small" label={`${circle.unresolved} external targets`} sx={{ height: 22, bgcolor: '#fdecea', color: '#8a2118' }} />
            </Tooltip>
          </Stack>

          {!circle.nodeApps.length ? emptyState('No application-to-application integrations captured yet. Add integrations to a fact sheet to see the circle map.') : (
            <div id="circlemap-print">
              <Paper sx={{ p: 1 }}>
                <svg
                  viewBox={`0 0 ${circle.size} ${circle.size}`}
                  width="100%"
                  style={{ maxHeight: 780, display: 'block', margin: '0 auto' }}
                  onMouseLeave={() => setHoverId(null)}
                >
                  <circle cx={circle.c} cy={circle.c} r={285} fill="none" stroke="#eef2f6" strokeWidth={1} />

                  {/* chords */}
                  {circle.links.map((l) => {
                    const p1 = circle.pos[l.from];
                    const p2 = circle.pos[l.to];
                    const active = !hoverId || hoverId === l.from || hoverId === l.to;
                    return (
                      <path
                        key={l.id}
                        d={`M ${p1.x.toFixed(1)} ${p1.y.toFixed(1)} Q ${circle.c} ${circle.c} ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`}
                        fill="none"
                        stroke={DIR_COLORS[l.direction]}
                        strokeWidth={active && hoverId ? 2.2 : 1.2}
                        strokeOpacity={active ? (hoverId ? 0.95 : 0.5) : 0.06}
                        strokeLinecap="round"
                      >
                        <title>{`${l.fromName} → ${l.toName} · ${l.direction}${l.protocol ? ` · ${l.protocol}` : ''}${l.dataType ? ` · ${l.dataType}` : ''}`}</title>
                      </path>
                    );
                  })}

                  {/* nodes + labels */}
                  {circle.nodeApps.map((a) => {
                    const p = circle.pos[a.id];
                    const active = !hoverId || hoverId === a.id
                      || (circle.neighbors.get(a.id) || EMPTY_SET).has(hoverId);
                    const deg = p.left ? p.deg + 180 : p.deg;
                    return (
                      <g
                        key={a.id}
                        style={{ cursor: 'pointer' }}
                        opacity={active ? 1 : 0.18}
                        onMouseEnter={() => setHoverId(a.id)}
                        onClick={() => open(a.id)}
                      >
                        <title>{`${a.name} · ${circle.degree[a.id] || 0} interface${(circle.degree[a.id] || 0) === 1 ? '' : 's'} · ${a.completion || 0}% complete`}</title>
                        <circle
                          cx={p.x}
                          cy={p.y}
                          r={hoverId === a.id ? 6.5 : 4.5}
                          fill={hoverId === a.id ? BLUE : NAVY}
                          stroke="#fff"
                          strokeWidth={1}
                        />
                        <text
                          x={p.lx}
                          y={p.ly}
                          fontSize={labelSize}
                          fontWeight={hoverId === a.id ? 700 : 500}
                          fill={hoverId === a.id ? BLUE : '#33414f'}
                          textAnchor={p.left ? 'end' : 'start'}
                          dominantBaseline="middle"
                          transform={`rotate(${deg.toFixed(1)} ${p.lx.toFixed(1)} ${p.ly.toFixed(1)})`}
                        >
                          {a.name.length > 34 ? `${a.name.slice(0, 33)}…` : a.name}
                        </text>
                      </g>
                    );
                  })}

                  {/* SVG has no 'sectionHeader.main' keyword: fill={HEADER} was an
                      invalid paint value, so this caption fell back to black and
                      vanished on the dark theme. Resolve the token to a colour. */}
                  <text x={circle.c} y={circle.c - 6} textAnchor="middle" fontSize={13} fontWeight={700}
                    fill={sectionHeaderColor(theme)}>
                    Interface Circle Map
                  </text>
                  <text x={circle.c} y={circle.c + 12} textAnchor="middle" fontSize={11} fill={GREY}>
                    {circle.links.length} application-to-application flows
                  </text>
                </svg>
              </Paper>
              <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1 }}>
                Hover an application to isolate its flows; click to open the fact sheet. Targets that do not
                match an application by name are counted as external and are not drawn.
              </Typography>
            </div>
          )}
        </Box>
      )}
    </Box>
  );
}
